import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import type {
  PaymentMethodCreateSchema,
  PaymentMethodQueryAllSchema,
  PaymentMethodQuerySchema,
  PaymentMethodUpdateSchema,
} from './schemas';

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['PAYMENT_METHODS'] });
};

export const useGetPaymentMethodsMutation: UseControlledMutation<PaymentMethodQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['PAYMENT_METHODS_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<PaymentMethodQueryAllSchema>({
            endpoint: '/api/v1/payment-methods',
            params: { search, per_page },
          });
          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export function useGetPaymentMethods(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['PAYMENT_METHODS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<PaymentMethodQueryAllSchema>({
        endpoint: '/api/v1/payment-methods',
        params: `${apiParamStr}`,
      });
      return res;
    },
  });
}

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, PaymentMethodCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/payment-methods',
        payload,
      }),
  });

export function useGetPaymentMethod(id: string) {
  return useSuspenseQuery({
    queryKey: ['PAYMENT_METHOD', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: PaymentMethodQuerySchema }>({
        endpoint: `/api/v1/payment-methods/${id}`,
        params: 'include=payment_method_fields',
        preventAuditLog: true,
      });

      return res;
    },
  });
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: PaymentMethodQuerySchema }, ApiError, Partial<PaymentMethodUpdateSchema>>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/payment-methods/${id}`,
        method: 'PUT',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['PAYMENT_METHOD_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/payment-methods/${id}`,
        method: 'DELETE',
        payload: {},
      }),
  });
